import { useState, useRef } from "react";
import { C } from "../../../constants";
import { Ic } from "../../../components/ui/Icons";
import { inputStyle, selectStyle, inputFocus, inputBlur } from "../../../components/ui/Components";
import { generateMultiEnclosureBOM } from "../calc/microSuppressionEngine";
import { generateBOMReport, generateBOMReportBase64 } from "../calc/pdfReportGenerator";

// ─── Shared form components ───
function Label({ en, es, required }) {
  return (<div style={{ marginBottom: 6 }}><span style={{ display: "block", fontSize: 13, fontWeight: 600, color: C.t1 }}>{en}{required && <span style={{ color: C.red, marginLeft: 3 }}>*</span>}</span>{es && <span style={{ display: "block", fontSize: 11.5, color: C.t3, fontStyle: "italic" }}>{es}</span>}</div>);
}
function Section({ title, titleEs }) {
  return (<div style={{ borderBottom: `2px solid ${C.navy}15`, paddingBottom: 8, marginTop: 32, marginBottom: 20 }}><h3 style={{ fontSize: 16, fontWeight: 700, color: C.navy, margin: 0 }}>{title}</h3>{titleEs && <span style={{ fontSize: 12, color: C.t3, fontStyle: "italic" }}>{titleEs}</span>}</div>);
}
function TF({ en, es, value, onChange, placeholder, type = "text", required, error, disabled, rows }) {
  return (<div style={{ marginBottom: 16 }}><Label en={en} es={es} required={required} />{rows ? (<textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={rows} style={{ ...inputStyle, resize: "vertical", minHeight: 70, borderColor: error ? C.red : undefined }} onFocus={inputFocus} onBlur={inputBlur} />) : (<input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} disabled={disabled} style={{ ...inputStyle, borderColor: error ? C.red : undefined, background: disabled ? C.grayBg : undefined }} onFocus={inputFocus} onBlur={inputBlur} />)}{error && <span style={{ fontSize: 11, color: C.red, marginTop: 2, display: "block" }}>{error}</span>}</div>);
}
function SF({ en, es, value, onChange, options, required, error }) {
  return (<div style={{ marginBottom: 16 }}><Label en={en} es={es} required={required} /><select value={value} onChange={e => onChange(e.target.value)} style={{ ...selectStyle, borderColor: error ? C.red : undefined }} onFocus={inputFocus} onBlur={inputBlur}><option value="">-- Select --</option>{options.map(o => <option key={o} value={o}>{o}</option>)}</select>{error && <span style={{ fontSize: 11, color: C.red, marginTop: 2, display: "block" }}>{error}</span>}</div>);
}
function UnitSelector({ value, onChange, required, error }) {
  return (<div style={{ marginBottom: 16 }}><Label en="Unit System" es="Sistema de Unidades" required={required} /><div style={{ display: "flex", gap: 8 }}>{["Metric (m, m³, °C)", "Imperial (ft, ft³, °F)"].map(o => { const s = value === o; return (<button key={o} type="button" onClick={() => onChange(o)} style={{ padding: "8px 18px", borderRadius: 8, border: `1.5px solid ${s ? C.navy : error ? C.red : C.border}`, background: s ? `${C.navy}10` : C.white, color: s ? C.navy : C.t2, fontSize: 13, cursor: "pointer", fontFamily: "inherit", fontWeight: s ? 600 : 400, flex: 1, textAlign: "center" }}>{o}</button>); })}</div>{error && <span style={{ fontSize: 11, color: C.red, marginTop: 4, display: "block" }}>{error}</span>}</div>);
}

// ─── Per-enclosure BOM mini-table ───
function EnclosureBOMTable({ r }) {
  return (
    <div style={{ marginBottom: 16, background: C.grayBg, borderRadius: 10, padding: "12px 16px", border: `1px solid ${C.border}` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <h4 style={{ fontSize: 14, fontWeight: 700, color: C.navy, margin: 0 }}>{r.name}</h4>
        <div style={{ display: "flex", gap: 16, fontSize: 12, color: C.t2 }}>
          <span>FK 5112: <strong style={{ color: C.navy }}>{r.agentLbs} lbs</strong></span>
          <span>Cylinder: <strong style={{ color: C.navy }}>{r.model}</strong> × {r.cylCount}</span>
          {r.tubingMeters > 0 && <span>Tubing: <strong>{r.tubingMeters}m</strong></span>}
          {r.nozzleCount > 0 && <span>Nozzles: <strong>{r.nozzleCount}</strong></span>}
        </div>
      </div>
      <div style={{ fontSize: 12 }}>
        {r.items.map((item, i) => (
          <div key={i} style={{ display: "flex", gap: 8, padding: "3px 0", borderBottom: i < r.items.length - 1 ? `1px solid ${C.border}` : "none" }}>
            <span style={{ width: 180, fontFamily: "monospace", color: C.info, fontWeight: 600, fontSize: 11 }}>{item.pn}</span>
            <span style={{ flex: 1, color: C.t2 }}>{item.desc}</span>
            <span style={{ width: 30, textAlign: "center", fontWeight: 600 }}>{item.qty}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Aggregated BOM table ───
function AggregatedBOMTable({ bom, totalAgent }) {
  return (
    <div style={{ marginTop: 20 }}>
      <h4 style={{ fontSize: 15, fontWeight: 700, color: C.navy, margin: "0 0 12px" }}>Aggregated Project BOM — Order Summary</h4>
      <div style={{ background: C.white, borderRadius: 12, border: `1px solid ${C.border}`, overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: "200px 1fr 70px", padding: "10px 16px", background: C.navy, color: C.white, fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
          <span>Part Number</span><span>Description</span><span style={{ textAlign: "center" }}>Qty</span>
        </div>
        {bom.map((row, i) => (
          <div key={i} style={{ display: "grid", gridTemplateColumns: "200px 1fr 70px", padding: "10px 16px", borderBottom: i < bom.length - 1 ? `1px solid ${C.border}` : "none", fontSize: 13, alignItems: "center",
            background: i % 2 === 0 ? C.white : C.grayBg }}>
            <span style={{ fontWeight: 600, color: C.info, fontFamily: "monospace", fontSize: 12 }}>{row.pn}</span>
            <span style={{ color: C.t1 }}>{row.desc}</span>
            <span style={{ textAlign: "center", fontWeight: 600, color: C.t1 }}>{row.qty}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SuccessModal({ onExit, onAnother }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
      <div style={{ background: C.white, borderRadius: 16, padding: "40px 36px", maxWidth: 420, textAlign: "center", boxShadow: "0 12px 48px rgba(0,0,0,0.15)" }}>
        <div style={{ width: 64, height: 64, borderRadius: "50%", background: "#D1FAE5", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", fontSize: 32 }}>✓</div>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: C.t1, margin: "0 0 8px" }}>Submitted for Quoting</h2>
        <p style={{ fontSize: 14, color: C.t2, margin: "0 0 4px" }}>Your estimation has been submitted for quoting and is now pending review.</p>
        <p style={{ fontSize: 13, color: C.t3, fontStyle: "italic", margin: "0 0 28px" }}>Su estimación ha sido enviada para cotización y está pendiente de revisión.</p>
        <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
          <button onClick={onExit} style={{ padding: "12px 28px", borderRadius: 10, border: `1px solid ${C.border}`, background: C.white, color: C.t1, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>Exit</button>
          <button onClick={onAnother} style={{ padding: "12px 28px", borderRadius: 10, border: "none", background: `linear-gradient(135deg,${C.navy} 0%,${C.navyLight} 100%)`, color: C.white, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Submit Another</button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════
const makeEnc = (i) => ({ id: i, name: `Enclosure ${i}`, l: "", w: "", h: "" });

export default function MicroSuppressionCalc({ user, onBack, onSubmitRequest, onExit, onUnsavedChange }) {
  const [form, setForm] = useState({
    projectName: "", projectLocation: "", country: user.country || "",
    designerName: user.name || "", email: user.email || "", phone: "", notes: "",
    unitSystem: "", elevation: "", minTemp: "", concentration: "",
    cylinderConfig: "", pressureSupervisor: "", detectionType: "", supervisableSolenoid: "",
    isAirtight: "",
  });
  const [enclosures, setEnclosures] = useState([makeEnc(1)]);
  const [errors, setErrors] = useState({});
  const [result, setResult] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showAbandon, setShowAbandon] = useState(false);
  const [dirty, setDirty] = useState(false);
  const resultRef = useRef(null);

  const set = (key, val) => {
    setDirty(true);
    if (onUnsavedChange) onUnsavedChange(true);
    setForm(prev => {
      const next = { ...prev, [key]: val };
      if (key === "cylinderConfig") { next.detectionType = ""; next.supervisableSolenoid = ""; }
      return next;
    });
    if (errors[key]) setErrors(prev => { const n = { ...prev }; delete n[key]; return n; });
  };

  const setEnc = (idx, key, val) => {
    setDirty(true);
    if (onUnsavedChange) onUnsavedChange(true);
    setEnclosures(prev => prev.map((e, i) => i === idx ? { ...e, [key]: val } : e));
  };

  const addEnclosure = () => {
    if (enclosures.length >= 10) return;
    setEnclosures(prev => [...prev, makeEnc(prev.length + 1)]);
  };

  const removeEnclosure = (idx) => {
    if (enclosures.length <= 1) return;
    setEnclosures(prev => {
      const next = prev.filter((_, i) => i !== idx);
      return next.map((e, i) => ({ ...e, id: i + 1, name: `Enclosure ${i + 1}` }));
    });
  };

  const isMetric = form.unitSystem.startsWith("Metric");
  const isPE = form.cylinderConfig === "Pre-Engineering";
  const showSolenoid = isPE && form.detectionType === "Active Aspirating";
  const effectiveConc = form.isAirtight === "No" && form.concentration ? parseFloat(form.concentration) * 1.8 : parseFloat(form.concentration) || 4.5;

  const getDetectionOptions = () => {
    if (form.cylinderConfig === "Direct" || form.cylinderConfig === "Indirect") return ["Thermal Tubing"];
    if (isPE) return ["Thermal Tubing", "Active Aspirating"];
    return [];
  };

  const validate = () => {
    const e = {};
    const req = (k, msg) => { if (!form[k] || !form[k].toString().trim()) e[k] = msg; };
    req("projectName", "Required"); req("projectLocation", "Required");
    req("unitSystem", "Required"); req("elevation", "Required");
    req("minTemp", "Required"); req("concentration", "Required");
    req("cylinderConfig", "Required"); req("pressureSupervisor", "Required");
    req("detectionType", "Required"); req("isAirtight", "Required");
    if (showSolenoid) req("supervisableSolenoid", "Required");
    // At least enclosure 1 must have dimensions
    if (!enclosures[0].l || !enclosures[0].w || !enclosures[0].h) e.enc1 = "Enclosure 1 dimensions are required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleCalculate = () => {
    if (!validate()) return;
    setCalculating(true);
    setTimeout(() => {
      const sharedConfig = {
        elevation: form.elevation, minTemp: form.minTemp, concentration: form.concentration,
        cylinderConfig: form.cylinderConfig, pressureSupervisor: form.pressureSupervisor === "Yes",
        detectionType: form.detectionType, supervisableSolenoid: form.supervisableSolenoid === "Yes",
        effectiveConc,
      };
      const res = generateMultiEnclosureBOM(enclosures, sharedConfig, isMetric);
      setResult(res);
      setCalculating(false);
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    }, 500);
  };

  const handleSubmitForQuoting = async () => {
    if (!result || result.error || submitting) return;
    setSubmitting(true);
    try {
      const pdf = generateBOMReportBase64(
        { ...form, company: user.company },
        { ...result, reportTitle: "Micro-Suppression BOM" },
        isMetric
      );
      const files = [{ name: pdf.fileName, mimeType: pdf.mimeType, base64: pdf.base64 }];
      const success = await onSubmitRequest({ ...form, solutionType: "System Estimation: Micro Suppression", submittedBy: user.name }, files);
      if (success) setShowSuccess(true);
    } catch (err) { console.error(err); alert("Submission failed."); }
    setSubmitting(false);
  };

  const handleReset = () => {
    setForm({ projectName: "", projectLocation: "", country: user.country || "", designerName: user.name || "", email: user.email || "", phone: "", notes: "", unitSystem: "", elevation: "", minTemp: "", concentration: "", cylinderConfig: "", pressureSupervisor: "", detectionType: "", supervisableSolenoid: "", isAirtight: "" });
    setEnclosures([makeEnc(1)]); setResult(null); setErrors({}); setShowSuccess(false); setDirty(false);
    if (onUnsavedChange) onUnsavedChange(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBack = () => { if (dirty) setShowAbandon(true); else onBack(); };
  const unitLabel = isMetric ? "m" : "ft";
  const tempLabel = isMetric ? "°C" : "°F";

  return (
    <div style={{ maxWidth: 900 }}>
      {showSuccess && <SuccessModal onExit={onExit || onBack} onAnother={handleReset} />}
      {showAbandon && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: C.white, borderRadius: 16, padding: "32px 36px", maxWidth: 400, textAlign: "center", boxShadow: "0 12px 48px rgba(0,0,0,0.15)" }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: C.t1, margin: "0 0 8px" }}>Unsaved Changes</h3>
            <p style={{ fontSize: 14, color: C.t2, margin: "0 0 24px" }}>You have unsaved changes. Are you sure you want to leave?</p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
              <button onClick={() => setShowAbandon(false)} style={{ padding: "10px 24px", borderRadius: 10, border: `1px solid ${C.border}`, background: C.white, color: C.t1, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>Continue Editing</button>
              <button onClick={onBack} style={{ padding: "10px 24px", borderRadius: 10, border: "none", background: C.red, color: C.white, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Abandon</button>
            </div>
          </div>
        </div>
      )}

      <button onClick={handleBack} style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", color: C.t3, fontSize: 13, fontFamily: "inherit", padding: "4px 0", marginBottom: 16 }}
        onMouseEnter={e => e.currentTarget.style.color = C.navy} onMouseLeave={e => e.currentTarget.style.color = C.t3}>
        <Ic.ChevronLeft /> Back to Estimation Tools
      </button>

      <div style={{ background: C.white, borderRadius: 16, border: `1px solid ${C.border}`, padding: "32px 36px", boxShadow: "0 2px 8px rgba(0,0,0,0.03)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 8 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: "#7C3AED12", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>🔧</div>
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: C.t1, margin: 0 }}>Micro-Suppression System Calculator</h2>
            <p style={{ fontSize: 13, color: C.t2, margin: 0 }}>FK 5112 Clean Agent — Multi-Enclosure</p>
          </div>
        </div>
        <p style={{ fontSize: 11, color: C.t3, margin: "0 0 24px" }}><span style={{ color: C.red }}>*</span> Required fields / Campos obligatorios</p>

        <Section title="Project Information" titleEs="Información del Proyecto" />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
          <TF en="Project Name" es="Nombre del Proyecto" value={form.projectName} onChange={v => set("projectName", v)} required error={errors.projectName} />
          <TF en="Project Location" es="Ubicación del Proyecto" value={form.projectLocation} onChange={v => set("projectLocation", v)} required error={errors.projectLocation} />
          <TF en="Country" es="País" value={form.country} onChange={v => set("country", v)} />
          <TF en="Designer Name" es="Nombre del Diseñador" value={form.designerName} onChange={v => set("designerName", v)} />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
          <TF en="Email" value={form.email} onChange={v => set("email", v)} />
          <TF en="Phone" value={form.phone} onChange={v => set("phone", v)} />
        </div>
        <TF en="Additional Notes" es="Notas Adicionales" value={form.notes} onChange={v => set("notes", v)} placeholder="Additional notes..." rows={3} />

        <Section title="Design Requirements" titleEs="Requisitos de Diseño" />
        <UnitSelector value={form.unitSystem} onChange={v => set("unitSystem", v)} required error={errors.unitSystem} />
        <div style={{ padding: "10px 14px", borderRadius: 8, background: "#EFF6FF", border: "1px solid #BFDBFE", marginBottom: 16 }}>
          <p style={{ fontSize: 12, color: "#1E40AF", margin: 0, lineHeight: 1.5 }}>
            <strong>Concentration Reference:</strong><br />
            Airtight enclosures — Class A & C (&lt;470V): min 4.5% | Class C (&gt;470V): min 6% | Class B: consult NFPA.<br />
            Non-airtight enclosures — Class A & C (&lt;470V): min 8.1% | Class C (&gt;470V): min 10.8% | Class B: consult NFPA.
          </p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0 20px" }}>
          <TF en={`Elevation (${unitLabel})`} es="Elevación" value={form.elevation} onChange={v => set("elevation", v)} placeholder="e.g. 500" required error={errors.elevation} />
          <TF en={`Min Temp (${tempLabel})`} es="Temp. Mínima" value={form.minTemp} onChange={v => set("minTemp", v)} placeholder="e.g. 25" required error={errors.minTemp} />
          <TF en="Design Concentration (%)" es="Concentración" value={form.concentration} onChange={v => set("concentration", v)} placeholder="e.g. 6" required error={errors.concentration} />
        </div>

        <SF en="Is the enclosure airtight?" es="¿El recinto es hermético?" value={form.isAirtight} onChange={v => set("isAirtight", v)} options={["Yes", "No"]} required error={errors.isAirtight} />

        <Section title="System Configuration" titleEs="Configuración del Sistema" />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
          <SF en="Cylinder Configuration" es="Configuración" value={form.cylinderConfig} onChange={v => set("cylinderConfig", v)} options={["Direct", "Indirect", "Pre-Engineering"]} required error={errors.cylinderConfig} />
          <SF en="Pressure Supervisor" es="Supervisor de Presión" value={form.pressureSupervisor} onChange={v => set("pressureSupervisor", v)} options={["Yes", "No"]} required error={errors.pressureSupervisor} />
        </div>
        {form.cylinderConfig && (
          <SF en="Detection Type" es="Tipo de Detección" value={form.detectionType} onChange={v => set("detectionType", v)} options={getDetectionOptions()} required error={errors.detectionType} />
        )}
        {showSolenoid && (
          <SF en="Supervisable Solenoid" es="Solenoide Supervisable" value={form.supervisableSolenoid} onChange={v => set("supervisableSolenoid", v)} options={["Yes", "No"]} required error={errors.supervisableSolenoid} />
        )}

        {/* ENCLOSURES */}
        <Section title="Enclosure Dimensions" titleEs="Dimensiones de los Recintos" />
        {enclosures.map((enc, idx) => (
          <div key={enc.id} style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <h4 style={{ fontSize: 13, fontWeight: 600, color: C.t1, margin: 0 }}>
                Enclosure {idx + 1} ({unitLabel}) {idx === 0 && <span style={{ color: C.red }}>*</span>}
              </h4>
              {idx > 0 && (
                <button onClick={() => removeEnclosure(idx)} style={{ background: "none", border: "none", cursor: "pointer", color: C.t3, fontSize: 12, fontFamily: "inherit" }}
                  onMouseEnter={e => e.currentTarget.style.color = C.red} onMouseLeave={e => e.currentTarget.style.color = C.t3}>
                  ✕ Remove
                </button>
              )}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0 20px" }}>
              <TF en="Length" es="Largo" value={enc.l} onChange={v => setEnc(idx, "l", v)} required={idx === 0} error={idx === 0 ? errors.enc1 : undefined} />
              <TF en="Width" es="Ancho" value={enc.w} onChange={v => setEnc(idx, "w", v)} />
              <TF en="Height" es="Alto" value={enc.h} onChange={v => setEnc(idx, "h", v)} />
            </div>
          </div>
        ))}

        {enclosures.length < 10 && (
          <button onClick={addEnclosure} style={{ display: "flex", alignItems: "center", gap: 6, padding: "10px 20px", borderRadius: 10, border: `1.5px dashed ${C.navy}40`, background: `${C.navy}05`, color: C.navy, fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "inherit", width: "100%", justifyContent: "center", transition: "all 0.2s" }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = C.navy; e.currentTarget.style.background = `${C.navy}10`; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = `${C.navy}40`; e.currentTarget.style.background = `${C.navy}05`; }}>
            + Add Enclosure ({enclosures.length}/10)
          </button>
        )}

        {Object.keys(errors).length > 0 && (
          <div style={{ marginTop: 24, padding: "12px 16px", borderRadius: 10, background: "#FEF2F2", border: "1px solid #FECACA" }}>
            <p style={{ fontSize: 13, color: C.red, fontWeight: 600, margin: 0 }}>Please complete all required fields before calculating.</p>
          </div>
        )}

        <div style={{ marginTop: 28, display: "flex", justifyContent: "center" }}>
          <button onClick={handleCalculate} disabled={calculating}
            style={{ padding: "14px 48px", borderRadius: 12, border: "none", background: `linear-gradient(135deg,${C.navy} 0%,${C.navyLight} 100%)`, color: C.white, fontSize: 16, fontWeight: 700, cursor: calculating ? "not-allowed" : "pointer", fontFamily: "inherit", boxShadow: `0 6px 24px ${C.navy}30`, opacity: calculating ? 0.75 : 1 }}>
            {calculating ? "Calculating..." : "CALCULATE / CALCULAR"}
          </button>
        </div>
      </div>

      {/* RESULTS */}
      {result && (
        <div ref={resultRef} style={{ marginTop: 24 }}>
          {result.error ? (
            <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 12, padding: "20px 24px" }}>
              <p style={{ fontSize: 15, color: C.red, fontWeight: 600, margin: 0 }}>{result.error}</p>
            </div>
          ) : (
            <div style={{ background: C.white, borderRadius: 16, border: `1px solid ${C.border}`, padding: "28px 36px" }}>
              {/* Summary */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 24 }}>
                <div style={{ background: `${C.navy}08`, borderRadius: 10, padding: "14px 16px", border: `1px solid ${C.navy}15` }}>
                  <div style={{ fontSize: 11, color: C.t3, textTransform: "uppercase" }}>Total FK 5112 Required</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: C.navy }}>{result.totalAgent} Lbs</div>
                </div>
                <div style={{ background: `${C.navy}08`, borderRadius: 10, padding: "14px 16px", border: `1px solid ${C.navy}15` }}>
                  <div style={{ fontSize: 11, color: C.t3, textTransform: "uppercase" }}>Standard Cylinder Fill Qty</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: C.navy }}>{result.enclosureResults.reduce((s, r) => s + r.cylCount, 0)}</div>
                </div>
                <div style={{ background: `${C.navy}08`, borderRadius: 10, padding: "14px 16px", border: `1px solid ${C.navy}15` }}>
                  <div style={{ fontSize: 11, color: C.t3, textTransform: "uppercase" }}>Enclosures</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: C.navy }}>{result.enclosureResults.length}</div>
                </div>
              </div>

              {/* Per-enclosure breakdown */}
              <h3 style={{ fontSize: 16, fontWeight: 700, color: C.navy, margin: "0 0 12px" }}>Per-Enclosure Breakdown</h3>
              {result.enclosureResults.map((r, i) => <EnclosureBOMTable key={i} r={r} />)}

              {/* Aggregated BOM */}
              <AggregatedBOMTable bom={result.aggregatedBom} totalAgent={result.totalAgent} />

              <div style={{ marginTop: 16, padding: "10px 14px", borderRadius: 8, background: "#FEF3C7", border: "1px solid #FDE68A" }}>
                <p style={{ fontSize: 12, color: "#92400E", margin: 0 }}>
                  <strong>Note:</strong> Always verify the system configuration with engineering. This is a preliminary estimate for budgeting purposes only.
                </p>
              </div>

              <div style={{ marginTop: 24, display: "flex", gap: 12, justifyContent: "center" }}>
                <button onClick={handleSubmitForQuoting} disabled={submitting}
                  style={{ padding: "12px 28px", borderRadius: 10, border: `1px solid ${C.border}`, background: C.white, color: C.t1, fontSize: 14, cursor: submitting ? "not-allowed" : "pointer", fontFamily: "inherit", fontWeight: 500, opacity: submitting ? 0.7 : 1 }}>
                  {submitting ? "Submitting..." : "Submit for Quoting"}
                </button>
                <button onClick={() => generateBOMReport({ ...form, company: user.company }, { ...result, reportTitle: "Micro-Suppression BOM" }, isMetric)}
                  style={{ padding: "12px 28px", borderRadius: 10, border: "none", background: `linear-gradient(135deg,${C.navy} 0%,${C.navyLight} 100%)`, color: C.white, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", boxShadow: `0 4px 16px ${C.navy}25` }}>
                  Generate PDF Report
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
